import { useTranslation } from 'next-i18next/pages';

import { Box, Button, Stack, Text, Title } from '@highlight-cp/core-ui';

import classes from './ai-chat-downloadable-file.module.css';

export interface AiChatDownloadableFileProps {
  filename: string;
  mimeType: string;
  content: string;
  formatLabel?: string;
  disclaimer?: string;
  title?: string;
  onDownloadFile: () => void;
}

const PREVIEW_MAX_CHARS = 480;

/**
 * Inline chat artifact for a model-authored downloadable file. Shows a short preview of the
 * same bytes the Download control will write — no second generation path.
 */
export const AiChatDownloadableFile = ({
  filename,
  mimeType,
  content,
  formatLabel,
  disclaimer,
  title,
  onDownloadFile,
}: AiChatDownloadableFileProps) => {
  const { t } = useTranslation();
  const preview =
    content.length > PREVIEW_MAX_CHARS ? `${content.slice(0, PREVIEW_MAX_CHARS)}…` : content;
  const heading = title?.trim() || filename;
  const downloadLabel = `${t('Download')} ${filename}`;
  // `group` (not `region`) so multiple artifacts in one chat do not collide as landmarks;
  // include the filename so each preview stays distinguishable to assistive tech.
  const previewLabel = `${t('File preview')}: ${filename}`;

  return (
    <Box className={classes.root}>
      <Stack gap="sm">
        <Stack gap={4}>
          <Title order={4}>{heading}</Title>
          <Text size="sm" c="dimmed">
            {formatLabel ? `${formatLabel} · ${filename}` : filename}
            {` · ${mimeType}`}
          </Text>
        </Stack>
        {disclaimer ? (
          <Text size="sm" c="dimmed">
            {disclaimer}
          </Text>
        ) : null}
        <Box className={classes.preview} tabIndex={0} aria-label={previewLabel} role="group">
          <Text size="xs" ff="Monaco, monospace">
            {preview}
          </Text>
        </Box>
        <Box>
          <Button size="xs" variant="default" onClick={onDownloadFile} aria-label={downloadLabel}>
            {t('Download')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default AiChatDownloadableFile;
